import * as Sentry from "@sentry/react"

declare const __BUILD_COMMIT__: string

let initialized = false

/**
 * Initialize Sentry error tracking.
 * Silently skips if VITE_SENTRY_DSN is not set - the app works fine without it.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.warn("[sentry] VITE_SENTRY_DSN not set - error tracking disabled")
    return
  }

  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || "development"

  Sentry.init({
    dsn,
    environment,
    release: typeof __BUILD_COMMIT__ !== "undefined" ? __BUILD_COMMIT__ : undefined,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],

    // Strip sensitive legal content from error reports
    beforeSend(event) {
      if (event.request?.data) {
        const data = event.request.data
        if (typeof data === "string" || typeof data === "object") {
          event.request.data = "[Filtered - may contain legal content]"
        }
      }

      // Scrub breadcrumb data that might contain document text or API keys
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.data) {
            for (const key of Object.keys(crumb.data)) {
              const lower = key.toLowerCase()
              if (
                lower.includes("text") ||
                lower.includes("content") ||
                lower.includes("body") ||
                lower.includes("note") ||
                lower.includes("privilege") ||
                lower.includes("key") ||
                lower.includes("token") ||
                lower.includes("secret")
              ) {
                crumb.data[key] = "[Filtered]"
              }
            }
          }
        }
      }

      return event
    },
  })

  initialized = true
}

/**
 * Capture an exception to Sentry if initialized.
 * Safe to call even when Sentry is not configured - it will no-op.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialized) {
    return
  }
  Sentry.captureException(error, context ? { extra: context } : undefined)
}
